import { useRouter } from 'expo-router';
import { useEffect, useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { PrimaryCTA, SkipLink } from '@/components/onboarding/Controls';
import { Eyebrow, H1, Screen, WhyLine } from '@/components/onboarding/Screen';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import type {
  Basement,
  ExteriorMaterial,
  Foundation,
  Garage,
  RoofMaterial,
  Sewer,
  WaterSource,
} from '@/db/types';
import { useOnboarding } from '@/store/onboarding';

const BASEMENT_OPTS: { v: Basement; label: string }[] = [
  { v: 'none', label: 'None' },
  { v: 'crawl', label: 'Crawl space' },
  { v: 'unfinished', label: 'Unfinished' },
  { v: 'finished', label: 'Finished' },
  { v: 'partial', label: 'Partial' },
];

const FOUNDATION_OPTS: { v: Foundation; label: string }[] = [
  { v: 'slab', label: 'Slab' },
  { v: 'crawl', label: 'Crawl' },
  { v: 'basement', label: 'Basement' },
  { v: 'pier', label: 'Pier' },
  { v: 'unsure', label: 'Not sure' },
];

const ROOF_OPTS: { v: RoofMaterial; label: string }[] = [
  { v: 'asphalt', label: 'Asphalt shingle' },
  { v: 'metal', label: 'Metal' },
  { v: 'tile', label: 'Tile' },
  { v: 'slate', label: 'Slate' },
  { v: 'wood', label: 'Wood shake' },
  { v: 'flat', label: 'Flat / membrane' },
  { v: 'unsure', label: 'Not sure' },
];

const EXTERIOR_OPTS: { v: ExteriorMaterial; label: string }[] = [
  { v: 'vinyl', label: 'Vinyl' },
  { v: 'brick', label: 'Brick' },
  { v: 'wood', label: 'Wood' },
  { v: 'stucco', label: 'Stucco' },
  { v: 'fiber-cement', label: 'Fiber-cement' },
  { v: 'stone', label: 'Stone' },
  { v: 'aluminum', label: 'Aluminum' },
  { v: 'log', label: 'Log' },
  { v: 'other', label: 'Other' },
];

const GARAGE_OPTS: { v: Garage; label: string }[] = [
  { v: 'none', label: 'None' },
  { v: '1-car', label: '1-car' },
  { v: '2-car', label: '2-car' },
  { v: '3-car', label: '3-car' },
  { v: 'detached', label: 'Detached' },
  { v: 'carport', label: 'Carport' },
];

const WATER_OPTS: { v: WaterSource; label: string }[] = [
  { v: 'city', label: 'City / municipal' },
  { v: 'well', label: 'Well' },
  { v: 'mixed', label: 'Mixed' },
  { v: 'unsure', label: 'Not sure' },
];

const SEWER_OPTS: { v: Sewer; label: string }[] = [
  { v: 'city', label: 'City' },
  { v: 'septic', label: 'Septic' },
  { v: 'shared', label: 'Shared' },
  { v: 'unsure', label: 'Not sure' },
];

export default function DetailScreen() {
  const router = useRouter();
  const state = useOnboarding();
  const applyDetailDefaults = useOnboarding((s) => s.applyDetailDefaults);

  // Apply smart chip defaults based on home_type + year_band BEFORE
  // seeding the local state below. The store's apply function only
  // fills fields the user hasn't touched, so coming back via the back
  // button preserves explicit choices.
  useEffect(() => {
    applyDetailDefaults();
  }, [applyDetailDefaults]);

  const [basement, setBasement] = useState<Basement | null>(state.basement);
  const [foundations, setFoundationsLocal] = useState<Foundation[]>(state.foundations);
  const [roofs, setRoofs] = useState<RoofMaterial[]>(state.roofMaterials);
  const [exteriors, setExteriors] = useState<ExteriorMaterial[]>(state.exteriorMaterials);
  const [garage, setGarage] = useState<Garage | null>(state.garage);
  const [water, setWater] = useState<WaterSource | null>(state.waterSource);
  const [sewer, setSewer] = useState<Sewer | null>(state.sewer);

  // Re-sync local state when the store updates from applyDetailDefaults
  // (initial mount, before local state was seeded). Without this, the
  // chip groups would briefly render "nothing selected" even though
  // the store already has defaults.
  useEffect(() => {
    if (state.basement && !basement) setBasement(state.basement);
    if (state.foundations.length > 0 && foundations.length === 0) {
      setFoundationsLocal(state.foundations);
    }
    if (state.roofMaterials.length > 0 && roofs.length === 0) {
      setRoofs(state.roofMaterials);
    }
    if (state.exteriorMaterials.length > 0 && exteriors.length === 0) {
      setExteriors(state.exteriorMaterials);
    }
    if (state.garage && !garage) setGarage(state.garage);
    if (state.waterSource && !water) setWater(state.waterSource);
    if (state.sewer && !sewer) setSewer(state.sewer);
    // We only sync ONCE on mount — after that, the user's local edits
    // are authoritative. The conditions above (`&& !basement`, length 0)
    // make this idempotent without an explicit ran-once flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.basement, state.foundations, state.roofMaterials,
    state.exteriorMaterials, state.garage, state.waterSource, state.sewer,
  ]);

  const commit = () => {
    state.setBasement(basement);
    state.setFoundations(foundations);
    state.setRoofMaterials(roofs);
    state.setExteriorMaterials(exteriors);
    state.setGarage(garage);
    state.setWaterSource(water);
    state.setSewer(sewer);
    router.push('/(onboarding)/generating' as never);
  };

  return (
    <Screen
      scroll
      footer={
        <>
          <PrimaryCTA label="Build my plan" onPress={commit} />
          <SkipLink label="Skip the rest — I'll fill it in later →" onPress={commit} />
        </>
      }>
      <Eyebrow>8 · Full Setup</Eyebrow>
      <H1>One more pass —{'\n'}home specifics.</H1>
      <WhyLine>
        We&apos;ve guessed the most common answer for your home shape and
        era. Tap any chip to change it — or just hit &ldquo;Build my plan&rdquo;
        if the guesses look right. You can pick MULTIPLE for roof, siding,
        and foundation if your home is mixed (brick + vinyl, etc.).
      </WhyLine>

      <PickGroup label="Basement" options={BASEMENT_OPTS} value={basement} onPick={setBasement} />
      <MultiPickGroup
        label="Foundation"
        sub="Split-levels can have multiple. Pick all that apply."
        options={FOUNDATION_OPTS_MULTI}
        values={foundations}
        onChange={setFoundationsLocal}
      />
      <MultiPickGroup
        label="Roof material"
        sub="Main + porch / addition can differ. Pick all that apply."
        options={ROOF_OPTS_MULTI}
        values={roofs}
        onChange={setRoofs}
      />
      <MultiPickGroup
        label="Exterior"
        sub="Brick front + vinyl sides? Pick every material on your home."
        options={EXTERIOR_OPTS_MULTI}
        values={exteriors}
        onChange={setExteriors}
      />
      <PickGroup label="Garage" options={GARAGE_OPTS} value={garage} onPick={setGarage} />
      <PickGroup label="Water source" options={WATER_OPTS} value={water} onPick={setWater} />
      <PickGroup label="Sewer" options={SEWER_OPTS} value={sewer} onPick={setSewer} />

    </Screen>
  );
}

/**
 * Multi-select chip group. Used for the structural attributes (roof,
 * exterior, foundation) where real homes are often mixed materials.
 * Visually mirrors PickGroup but each chip toggles into/out of the
 * array independently.
 */
function MultiPickGroup<T extends string>({
  label,
  sub,
  options,
  values,
  onChange,
}: {
  label: string;
  sub?: string;
  options: { v: T; label: string }[];
  values: T[];
  onChange: (next: T[]) => void;
}) {
  const toggle = (v: T) => {
    if (values.includes(v)) {
      onChange(values.filter((x) => x !== v));
    } else {
      onChange([...values, v]);
    }
  };
  return (
    <View style={styles.group}>
      <Text style={styles.label}>{label.toUpperCase()}</Text>
      {sub ? <Text style={styles.sub}>{sub}</Text> : null}
      <View style={styles.chips}>
        {options.map((o) => {
          const sel = values.includes(o.v);
          return (
            <Pressable
              key={o.v}
              onPress={() => toggle(o.v)}
              style={({ pressed }) => [
                styles.chip,
                sel && styles.chipSel,
                pressed && { opacity: 0.85 },
              ]}>
              <Text style={[styles.chipText, sel && styles.chipTextSel]}>{o.label}</Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

/**
 * Multi-select options. Same shape as the single-pick options used
 * elsewhere on the page, minus the "Not sure" entries (multi-pick
 * implies you know what you have, otherwise just leave it empty).
 */
const ROOF_OPTS_MULTI: { v: RoofMaterial; label: string }[] = [
  { v: 'asphalt', label: 'Asphalt shingle' },
  { v: 'metal', label: 'Metal' },
  { v: 'tile', label: 'Tile' },
  { v: 'slate', label: 'Slate' },
  { v: 'wood', label: 'Wood shake' },
  { v: 'flat', label: 'Flat / membrane' },
];

const EXTERIOR_OPTS_MULTI: { v: ExteriorMaterial; label: string }[] = [
  { v: 'vinyl', label: 'Vinyl' },
  { v: 'brick', label: 'Brick' },
  { v: 'wood', label: 'Wood' },
  { v: 'stucco', label: 'Stucco' },
  { v: 'fiber-cement', label: 'Fiber-cement' },
  { v: 'stone', label: 'Stone' },
  { v: 'aluminum', label: 'Aluminum' },
  { v: 'log', label: 'Log' },
];

const FOUNDATION_OPTS_MULTI: { v: Foundation; label: string }[] = [
  { v: 'slab', label: 'Slab' },
  { v: 'crawl', label: 'Crawl' },
  { v: 'basement', label: 'Basement' },
  { v: 'pier', label: 'Pier' },
];

function PickGroup<T extends string>({
  label,
  options,
  value,
  onPick,
}: {
  label: string;
  options: { v: T; label: string }[];
  value: T | null;
  onPick: (v: T | null) => void;
}) {
  return (
    <View style={styles.group}>
      <Text style={styles.label}>{label.toUpperCase()}</Text>
      <View style={styles.chips}>
        {options.map((o) => {
          const sel = value === o.v;
          return (
            <Pressable
              key={o.v}
              onPress={() => onPick(sel ? null : o.v)}
              style={({ pressed }) => [
                styles.chip,
                sel && styles.chipSel,
                pressed && { opacity: 0.85 },
              ]}>
              <Text style={[styles.chipText, sel && styles.chipTextSel]}>{o.label}</Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  group: {
    marginTop: Spacing.four,
  },
  label: {
    fontSize: 12,
    fontFamily: Fonts.sansBold,
    color: Palette.inkFaint,
    letterSpacing: 0.8,
    marginBottom: 9,
  },
  sub: {
    fontFamily: Fonts.sansMedium, fontSize: 13, color: Palette.inkSoft,
    marginTop: -4, marginBottom: 9, lineHeight: 17,
  },
  chips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 7,
  },
  chip: {
    paddingHorizontal: 14,
    paddingVertical: 10,
    borderRadius: Radius.pill,
    backgroundColor: Palette.card,
    borderWidth: 1.5,
    borderColor: Palette.line,
  },
  chipSel: {
    backgroundColor: Palette.forest,
    borderColor: Palette.forest,
  },
  chipText: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
  chipTextSel: {
    color: Palette.cream,
  },
});
